/**
 * Emojis Unicode para o seletor e o `:atalho:`: nomes e palavras em
 * português (emojibase) e os atalhos em inglês que o Discord usa
 * (`:thumbsup:`). Cada plataforma carrega os dados do emojibase do jeito
 * dela; daqui saem a lista montada, a busca e os atalhos.
 */
export type EmojiUnicode = {
  unicode: string;
  nome: string;
  palavras: string[];
  atalhos: string[];
  grupo: number;
  ordem: number;
};

export const GRUPOS_EMOJI = [
  'Sorrisos e emoções',
  'Pessoas',
  'Componentes',
  'Animais e natureza',
  'Comida e bebida',
  'Viagens e lugares',
  'Atividades',
  'Objetos',
  'Símbolos',
  'Bandeiras',
];

/** Os da barra de reação rápida (C6), quando ainda não há usados com frequência. */
export const EMOJIS_RAPIDOS = ['👍', '✅', '👀', '❤️', '😂', '🙏'];

export type EmojibaseCompacto = {
  unicode: string;
  label: string;
  hexcode: string;
  tags?: string[];
  group?: number;
  order?: number;
};

/** A lista do seletor a partir dos dados do emojibase (pt + atalhos do iamcal). */
export function montarEmojis(
  compacto: EmojibaseCompacto[],
  atalhos: Record<string, string | string[]>,
): EmojiUnicode[] {
  return compacto
    .filter((item) => item.group !== undefined && item.group !== 2)
    .map((item) => {
      const codigos = atalhos[item.hexcode] ?? [];
      return {
        unicode: item.unicode,
        nome: item.label,
        palavras: item.tags ?? [],
        atalhos: Array.isArray(codigos) ? codigos : [codigos],
        grupo: item.group ?? 9,
        ordem: item.order ?? 0,
      };
    })
    .sort((a, b) => a.ordem - b.ordem);
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Busca por nome, atalho ou palavra (sem acento, sem diferenciar
 * maiúsculas): primeiro o nome que começa com o termo, depois o atalho,
 * depois as palavras; por último o que só contém o termo.
 */
export function buscarEmojis(
  lista: EmojiUnicode[],
  termo: string,
  limite = 60,
): EmojiUnicode[] {
  const busca = normalizar(termo.trim());
  if (!busca) return lista.slice(0, limite);
  const faixas: EmojiUnicode[][] = [[], [], [], []];
  for (const emoji of lista) {
    const nome = normalizar(emoji.nome);
    const atalhos = emoji.atalhos.map(normalizar);
    const palavras = emoji.palavras.map(normalizar);
    if (
      nome.startsWith(busca) ||
      nome.split(' ').some((parte) => parte.startsWith(busca))
    ) {
      faixas[0].push(emoji);
    } else if (atalhos.some((atalho) => atalho.startsWith(busca))) {
      faixas[1].push(emoji);
    } else if (palavras.some((palavra) => palavra.startsWith(busca))) {
      faixas[2].push(emoji);
    } else if (
      [nome, ...atalhos, ...palavras].some((texto) => texto.includes(busca))
    ) {
      faixas[3].push(emoji);
    }
  }
  return faixas.flat().slice(0, limite);
}

let ultimoMapa: { lista: EmojiUnicode[]; mapa: Map<string, string> } | null =
  null;

/** `:atalho:` → emoji, para a conversão no envio (montado uma vez só). */
export function mapaDeAtalhos(lista: EmojiUnicode[]): Map<string, string> {
  if (ultimoMapa?.lista === lista) return ultimoMapa.mapa;
  const mapa = new Map<string, string>();
  for (const emoji of lista) {
    for (const atalho of emoji.atalhos)
      mapa.set(atalho.toLowerCase(), emoji.unicode);
  }
  ultimoMapa = { lista, mapa };
  return mapa;
}
